import React from "react";
import { IEsvcsEnrollment } from "csd.phoenix.models/EsvcsEnrollment";
import { Button } from "@blueprintjs/core";
import { EmailDialog } from "../../shared/dialogs/EmailDialog";
import { ProvisionDialog } from "../../shared/dialogs/ProvisionDialog";
import { DeactivateDialog } from "../../shared/dialogs/DeactivateDialog";
import useDialogState from "../../shared/useDialogState";
import { EsvcsEnrollmentStatuses } from 'csd.phoenix.models/EsvcsEnrollmentStatuses';
import { DpmsTypes } from 'csd.phoenix.models/DpmsTypes';
import { EsvcsProductOfferings } from "csd.phoenix.models/EsvcsProductOfferings";
import { IconNames } from "@blueprintjs/icons";
import { FinalizeConversionDialog } from "../../shared/dialogs/FinalizeConversionDialog";
import useAuthService from '../../../services/authService';
import useAppConfig from '../../shared/useAppConfig';
import styles from './styles.module.scss';

interface IProps {
 enrollment: IEsvcsEnrollment;
 onRefresh?: () => void;
}

const StatusUtils: React.FC<IProps> = ({enrollment, onRefresh}) => {
  const [emailIsOpen, openEmail, closeEmail] = useDialogState();
  const [provisionIsOpen, openProvision, closeProvision] = useDialogState();
  const [finalizeIsOpen, openFinalize, closeFinalize] = useDialogState();
  const [deactivateIsOpen, openDeactivate, closeDeactivate] = useDialogState();
  const { isInRole } = useAuthService();
  const { azureAd: { employee } } = useAppConfig();
  const isPartnerAdapterEnrollment = enrollment.ProductOffering === EsvcsProductOfferings.PartnerAdapter;
  const enableReprovisioning = (enrollment.EnrollmentStatus === EsvcsEnrollmentStatuses.Confirmed)
    || ((enrollment.EnrollmentStatus > EsvcsEnrollmentStatuses.Confirmed)
    && (enrollment.EnrollmentStatus <= EsvcsEnrollmentStatuses.GatewayProvisioning)
      && (enrollment.DpmsType !== DpmsTypes.CMP));

  const enableResendingEmail =
    (enrollment.EnrollmentStatus < EsvcsEnrollmentStatuses.SenseiDBProvisioning)
    || ((enrollment.EnrollmentStatus === EsvcsEnrollmentStatuses.GatewayProvisioned && !enrollment.IsHosted)
      || (enrollment.EnrollmentStatus === EsvcsEnrollmentStatuses.Completed));
  // Add admin role to allow for testing of deactivate functionality. 
  const canShowDeactivate = isInRole([employee.roles.support, employee.roles.admin]);
  //const canShowDeactivate = isInRole([employee.roles.support]);
  
  // Prevent deactivation of automated conversion enrollments once completed
  const isCompletedAutomatedConversion = 
    (enrollment.ProductOffering === EsvcsProductOfferings.AutomatedConversion) 
    && (enrollment.EnrollmentStatus >= EsvcsEnrollmentStatuses.Completed);
  
  const deactivateDisabled = isCompletedAutomatedConversion || [
    EsvcsEnrollmentStatuses.SenseiSyncInitiated,
    EsvcsEnrollmentStatuses.CloudSyncInitiated,
    EsvcsEnrollmentStatuses.GatewayProvisioning,
    EsvcsEnrollmentStatuses.SenseiDBProvisioning
  ].includes(enrollment.EnrollmentStatus);

  const handleDeactivateSuccess = () => {
    closeDeactivate();
    if (onRefresh) {
      onRefresh();
    }
  };
  const enableConversionFinalize =
    (enrollment.ProductOffering === EsvcsProductOfferings.AutomatedConversion)
    && (enrollment.EnrollmentStatus === EsvcsEnrollmentStatuses.Completed);

  const handleFinalizeSuccess = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  return (
    <div className={styles.action_buttons_container}>
      <Button icon={IconNames.Envelope}
        intent="none"
        title="Re-send Email"
        minimal={true}
        onClick={openEmail}
        disabled={isPartnerAdapterEnrollment || !enableResendingEmail}>
      </Button>

      {!isPartnerAdapterEnrollment && emailIsOpen ?
        <EmailDialog isOpen={true}
          onClose={closeEmail}
          enrollment={enrollment}>
        </EmailDialog>
        : null}

      <Button icon={IconNames.CloudUpload}
        intent="none"
        title="Resume/Restart Provisioning"
        minimal={true}
        onClick={openProvision}
        disabled={isPartnerAdapterEnrollment || !enableReprovisioning}>
      </Button>

      {!isPartnerAdapterEnrollment && provisionIsOpen ?
        <ProvisionDialog
          isOpen={true}
          onClose={closeProvision}
          enrollment={enrollment}>
        </ProvisionDialog>
        : null}


      <Button icon={IconNames.FlowEnd}
        intent="none"
        title="Finalize Automated Conversion / Customer is Live on Sensei"
        minimal={true}
        onClick={openFinalize}
        disabled={isPartnerAdapterEnrollment || !enableConversionFinalize}>
      </Button>

      {!isPartnerAdapterEnrollment && finalizeIsOpen ?
        <FinalizeConversionDialog
          isOpen={true}
          onClose={closeFinalize}
          onSuccess={handleFinalizeSuccess}
          enrollment={enrollment}>
        </FinalizeConversionDialog>
        : null}

      {canShowDeactivate && (
        <Button icon={IconNames.Trash}
          intent="none"
          title="Deactivate Enrollment"
          minimal={true}
          onClick={openDeactivate}
          disabled={deactivateDisabled}>
        </Button>
      )}

      {deactivateIsOpen ?
        <DeactivateDialog
          isOpen={true}
          onClose={closeDeactivate}
          enrollment={enrollment}
          onSuccess={handleDeactivateSuccess}>
        </DeactivateDialog>
        : null}
    </div>
  );
}

export default StatusUtils;
